import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { APPLICATION_ROUTES, Endpoint, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { Avatar, Button, FetchAPI, FieldAPI, Panel, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { ButtonTheme, Color, LayoutFlexDirection, LayoutJustify } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnCancel, OnClose, OnDynamicContentRender, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, SessionStorageService } from '@philips-emr/tws-core/lib/services';
import { format } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { FragmentNames, TechnicalVisitAliases } from '../../codegen';
import BaseController from '../BaseController';

interface TechnicalVisit {
  ieStatus: TechnicalVisitStatus,
  dsStatus: string,
  nrSeqAppointment: number
  suggestedDate: string,
  nrSeqVisit: number,
  dateTimeStamp: string,
}

interface RejectAppointment {
  message: string,
}

enum TechnicalVisitStatus {
  PENDING = 0,
  AWAITING_APPOINTMENT = 1,
  AWAITING_CONFIRMATION = 2,
  SCHEDULED = 3,
  CLOSED = 4,
  CANCELED = 5,
  ONGOING = 6,
  ANALYSIS = 7,
  EVALUATING_INCONSISTENCIES = 8,
}

@Controller({ fragment: FragmentNames.TECHNICAL_VISIT })
class TechnicalVisitController extends BaseController {

  @Inject(TechnicalVisitAliases.PANEL_MODAL)
  private panelModal: PanelAPI;

  @Inject(TechnicalVisitAliases.FETCH_APPOINTMENTS)
  private fetchAppointmens: FetchAPI;

  @Inject(TechnicalVisitAliases.FETCH_VISIT)
  private fetchVisit: FetchAPI;

  @Inject(TechnicalVisitAliases.PANEL_APPOINTMENTS)
  private panelAppointments: PanelAPI;

  @OnMount(TechnicalVisitAliases.FIELD_DATE)
  onMountFieldDate({ target }: MountEvent): void {
    const api = target as FieldAPI;
    const { dateTimeStamp } = api.getData() as unknown as TechnicalVisit;
    api.setVisible(!!dateTimeStamp);
  }

  @OnDynamicContentRender(TechnicalVisitAliases.BUTTOMS)
  onRenderButtonRefuse(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, nrSeqAppointment } = data as unknown as TechnicalVisit;

    if (ieStatus == 1) {
      render(() =>
        // @ts-ignore
        <Panel justifyContent={LayoutJustify.END} flexDirection={LayoutFlexDirection.ROW}>
          <Button
            key={MessageService.translate('pa.refuse')}
            onClick={this.refuseAppointment(nrSeqAppointment)}
            theme={ButtonTheme.QUIET_EMPHASIS}
            value={MessageService.translate('pa.refuse')} />
          <Button
            key={MessageService.translate('pa.confirmation')}
            onClick={this.confirmAppointment(nrSeqAppointment)}
            theme={ButtonTheme.PRIMARY}
            value={MessageService.translate('pa.confirmation')} />
        </Panel>
      );
    }
  }

  @OnDynamicContentRender(TechnicalVisitAliases.AVATAR)
  onRenderAvatar(event: RenderEvent): void {
    const { data, render } = event;
    const { suggestedDate } = data as TechnicalVisit;
    const formmatedDate = format('date', suggestedDate, { type: 'timestamp' });
    render((): ReactElement => <Avatar
      image="/static/assets/calendar_clock_mobile.svg"
      title={MessageService.translate('pa.visit-scheduling')}
      subtitle={formmatedDate} />);
  }

  @OnClose(TechnicalVisitAliases.MODAL_REFUSE)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnCancel(TechnicalVisitAliases.FORM_MESSAGE)
  onCancelForm(): void {
    this.panelModal.setVisible(false);
  }

  @OnSubmit(TechnicalVisitAliases.FORM_MESSAGE)
  onSubmitReject(event: SubmitEvent): void {
    const nrSeqAppointment = this.context.get('nrSeqAppointment');
    const { message } = event.record as RejectAppointment;
    void HttpService.post(Endpoint.POST.REJECT_APPOINTMENT, { nrSeqAppointment, message }).then(() => {
      this.fetchAppointmens.forceFetch();
      this.fetchVisit.forceFetch();
      ToastServiceProviderAccreditation.success(MessageService.translate('pa.reject-appointment-success-message'));
      this.panelModal.setVisible(false);
    });
  }

  @OnAfterFetch(TechnicalVisitAliases.FETCH_APPOINTMENTS)
  OnAfterFetch(event: FetchEvent): void {
    const data = event.data;
    if (isEmptyArrayOrObject(data)) {
      this.panelAppointments.setVisible(false);
    }
  }

  @OnDynamicContentRender(TechnicalVisitAliases.HPMS_CUSTOM_CARD_VISIT)
  public onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as unknown as TechnicalVisit;
    const stampVisit = this.getStamp(ieStatus, dsStatus);

    if (stampVisit) {
      render((): ReactElement => stampVisit);
    }

  }

  @OnSelectItem(TechnicalVisitAliases.DECK_VISIT)
  onClickCardVisit({ item }: SelectItemEvent<TechnicalVisit>): void {
    SessionStorageService.removeItem('ieStatus');
    const { nrSeqVisit, ieStatus } = item;
    SessionStorageService.setItem('ieStatus', ieStatus);
    this.historyService.push(APPLICATION_ROUTES.TECHNICAL_VISIT_DETAILS, { nrSeqVisit });
  }

  private refuseAppointment(nrSeqAppointment: number): () => void {
    return () => {
      this.panelModal.setVisible(true);
      this.context.set({ nrSeqAppointment });
    };
  }

  private confirmAppointment(nrSeqAppointment: number): () => void {
    return () => {
      void HttpService.post(Endpoint.POST.CONFIRM_APPOINTMENT, { nrSeqAppointment }).then(() => {
        this.fetchAppointmens.forceFetch();
        this.fetchVisit.forceFetch();
        ToastServiceProviderAccreditation.success(MessageService.translate('pa.confirm-appointment-sucess-message'));
      });
    };
  }

  private getStamp(ieStatus: TechnicalVisitStatus, label: string): ReactElement | null {
    let stampProps = null;

    switch (ieStatus) {
      case TechnicalVisitStatus.AWAITING_APPOINTMENT:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label };
        break;
      case TechnicalVisitStatus.AWAITING_CONFIRMATION:
        stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label };
        break;
      case TechnicalVisitStatus.SCHEDULED:
        stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label };
        break;
      case TechnicalVisitStatus.CLOSED:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
        break;
      case TechnicalVisitStatus.CANCELED:
        stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
        break;
      case TechnicalVisitStatus.ONGOING:
        stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label };
        break;
      case TechnicalVisitStatus.ANALYSIS:
        stampProps = { className: 'c-form-stamp__stamp--color', icon: StampIcon.CLOCK, label };
        break;
      case TechnicalVisitStatus.EVALUATING_INCONSISTENCIES:
        stampProps = { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label };
        break;
      default:
        return null;
    }

    return <Stamp {...stampProps} />;
  }
}

export {
  TechnicalVisitController,
  TechnicalVisitStatus,
};
